/* ==========================================================================
   LifeCare — theme.js
   Core runtime. Vanilla ES2020, custom elements, zero dependencies.
   Loaded with `defer` from layout/theme.liquid.

   Global events:
     lc:cart:updated   detail: cart JSON      — fired after any cart mutation
     lc:drawer:open    detail: { id }         — fired when a drawer opens
     lc:variant:change detail: { variant }    — fired by <lc-variant-picker>
   ========================================================================== */

(() => {
  'use strict';

  /* ------ Utilities ------------------------------------------------------ */

  /**
   * @param {Function} fn
   * @param {number} wait
   * @returns {Function}
   */
  const debounce = (fn, wait = 200) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), wait);
    };
  };

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  /** Announce a message to screen readers via the global live region. */
  const announce = (message) => {
    const region = document.getElementById('lc-live-region');
    if (!region) return;
    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = message;
    });
  };

  /* ------ Cart API --------------------------------------------------------- */

  const CartAPI = {
    /** Sections every cart mutation should re-render. */
    sectionIds() {
      return [...document.querySelectorAll('[data-lc-cart-section]')]
        .map((el) => el.dataset.lcCartSection)
        .filter(Boolean);
    },

    async request(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.description || data.message || 'Cart error');
      }
      return data;
    },

    async add(items) {
      const data = await this.request(
        window.Shopify?.routes?.root
          ? `${window.Shopify.routes.root}cart/add.js`
          : '/cart/add.js',
        { items, sections: this.sectionIds().join(',') }
      );
      await this.refresh(data.sections);
      return data;
    },

    async change(line, quantity) {
      const data = await this.request('/cart/change.js', {
        line,
        quantity,
        sections: this.sectionIds().join(',')
      });
      this.renderSections(data.sections);
      document.dispatchEvent(
        new CustomEvent('lc:cart:updated', { detail: data })
      );
      return data;
    },

    async update(body) {
      const data = await this.request('/cart/update.js', {
        ...body,
        sections: this.sectionIds().join(',')
      });
      this.renderSections(data.sections);
      document.dispatchEvent(
        new CustomEvent('lc:cart:updated', { detail: data })
      );
      return data;
    },

    /** /cart/add.js returns the added line, not the cart — fetch the cart. */
    async refresh(sections) {
      this.renderSections(sections);
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' }
      });
      const cart = await response.json();
      document.dispatchEvent(
        new CustomEvent('lc:cart:updated', { detail: cart })
      );
      return cart;
    },

    /** Swap innerHTML of registered cart sections from Section Rendering API. */
    renderSections(sections) {
      if (!sections) return;
      for (const el of document.querySelectorAll('[data-lc-cart-section]')) {
        const id = el.dataset.lcCartSection;
        if (!sections[id]) continue;
        const dom = new DOMParser().parseFromString(sections[id], 'text/html');
        const next = dom.querySelector(`[data-lc-cart-section="${id}"]`);
        if (next) el.innerHTML = next.innerHTML;
      }
    }
  };

  window.LcCart = CartAPI;

  /* Cart count bubbles stay in sync everywhere. */
  document.addEventListener('lc:cart:updated', (event) => {
    const count = event.detail?.item_count;
    if (typeof count !== 'number') return;
    for (const el of document.querySelectorAll('[data-lc-cart-count]')) {
      el.textContent = count;
      el.toggleAttribute('hidden', count === 0);
    }
  });

  /* ------ <lc-drawer> — focus-trapped panel / bottom sheet ------------------- */

  class LcDrawer extends HTMLElement {
    #trigger = null;
    #keydownHandler = this.#onKeydown.bind(this);

    connectedCallback() {
      this.setAttribute('role', 'dialog');
      this.setAttribute('aria-modal', 'true');
      this.setAttribute('tabindex', '-1');
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-lc-close]')) this.close();
      });
    }

    open(trigger) {
      if (this.hasAttribute('open')) return;
      this.#trigger = trigger || document.activeElement;
      this.setAttribute('open', '');
      document.body.setAttribute('data-lc-overlay', '');
      document.addEventListener('keydown', this.#keydownHandler);
      const target = this.querySelector('[autofocus]') || this;
      requestAnimationFrame(() => target.focus({ preventScroll: true }));
      document.dispatchEvent(
        new CustomEvent('lc:drawer:open', { detail: { id: this.id } })
      );
    }

    close() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      document.removeEventListener('keydown', this.#keydownHandler);
      if (!document.querySelector('lc-drawer[open]')) {
        document.body.removeAttribute('data-lc-overlay');
      }
      this.#trigger?.focus({ preventScroll: true });
      this.#trigger = null;
    }

    #onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...this.querySelectorAll(FOCUSABLE)].filter(
        (el) => el.offsetParent !== null
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }
  customElements.define('lc-drawer', LcDrawer);

  /* Any element can open/close a drawer declaratively. */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-lc-open]');
    if (opener) {
      event.preventDefault();
      document.getElementById(opener.dataset.lcOpen)?.open(opener);
      return;
    }
    if (event.target.closest('.lc-overlay')) {
      for (const drawer of document.querySelectorAll('lc-drawer[open]')) {
        drawer.close();
      }
    }
  });

  /* ------ <lc-quantity> — stepper -------------------------------------------- */

  class LcQuantity extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', (event) => {
        const button = event.target.closest('button');
        if (!button || !this.input) return;
        event.preventDefault();
        const delta = button.name === 'plus' ? 1 : -1;
        const min = Number(this.input.min || 0);
        const next = Math.max(min, Number(this.input.value || 0) + delta);
        if (next === Number(this.input.value)) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  customElements.define('lc-quantity', LcQuantity);

  /* ------ <lc-slider> — scroll-snap carousel controls -------------------------- */

  class LcSlider extends HTMLElement {
    #observer = null;

    connectedCallback() {
      this.track = this.querySelector('[data-lc-slider-track]');
      this.prev = this.querySelector('[data-lc-slider-prev]');
      this.next = this.querySelector('[data-lc-slider-next]');
      this.dots = this.querySelector('[data-lc-slider-dots]');
      if (!this.track) return;

      this.slides = [...this.track.children];
      this.prev?.addEventListener('click', () => {
        this.stopAutoplay();
        this.#scrollBy(-1);
      });
      this.next?.addEventListener('click', () => {
        this.stopAutoplay();
        this.#scrollBy(1);
      });
      this.track.addEventListener(
        'scroll',
        debounce(() => this.#sync(), 60),
        { passive: true }
      );
      this.#buildDots();
      this.#sync();

      this.autoplay = this.getAttribute('data-autoplay') === 'true';
      this.autoplaySpeed = parseInt(this.getAttribute('data-autoplay-speed')) * 1000 || 5000;
      this.timer = null;

      if (this.autoplay && this.slides.length > 1) {
        this.startAutoplay();
        this.addEventListener('mouseenter', () => this.stopAutoplay());
        this.addEventListener('mouseleave', () => this.startAutoplay());
        this.addEventListener('touchstart', () => this.stopAutoplay(), { passive: true });
        this.addEventListener('touchend', () => this.startAutoplay(), { passive: true });
      }
    }

    startAutoplay() {
      this.stopAutoplay();
      this.timer = setInterval(() => {
        const current = this.#index();
        const nextIndex = (current + 1) % this.slides.length;
        this.scrollToIndex(nextIndex);
      }, this.autoplaySpeed);
    }

    stopAutoplay() {
      if (this.timer) clearInterval(this.timer);
    }

    scrollToIndex(i) {
      const slide = this.slides[i];
      if (!slide) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.track.scrollTo({
        left: (slide.offsetWidth + gap) * i * (this.#isRTL() ? -1 : 1),
        behavior: prefersReducedMotion.matches ? 'auto' : 'smooth'
      });
    }

    disconnectedCallback() {
      this.#observer?.disconnect();
      this.stopAutoplay();
    }

    #scrollBy(direction) {
      const slide = this.slides[0];
      if (!slide) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.track.scrollBy({
        left: (slide.offsetWidth + gap) * direction * (this.#isRTL() ? -1 : 1),
        behavior: prefersReducedMotion.matches ? 'auto' : 'smooth'
      });
    }

    #isRTL() {
      return getComputedStyle(this).direction === 'rtl';
    }

    #index() {
      const slide = this.slides[0];
      if (!slide) return 0;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return Math.round(
        Math.abs(this.track.scrollLeft) / (slide.offsetWidth + gap)
      );
    }

    #sync() {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      const pos = Math.abs(this.track.scrollLeft);
      if (this.prev) this.prev.disabled = pos <= 2;
      if (this.next) this.next.disabled = pos >= max;
      if (this.dots) {
        const current = this.#index();
        [...this.dots.children].forEach((dot, i) => {
          dot.setAttribute('aria-current', i === current ? 'true' : 'false');
        });
      }
    }

    #buildDots() {
      if (!this.dots) return;
      this.dots.innerHTML = '';
      this.slides.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'lc-slider-dots__dot';
        dot.setAttribute(
          'aria-label',
          `${this.dataset.slideLabel || 'Slide'} ${i + 1}`
        );
        dot.addEventListener('click', () => {
          this.slides[i].scrollIntoView({
            behavior: prefersReducedMotion.matches ? 'auto' : 'smooth',
            block: 'nearest',
            inline: 'start'
          });
        });
        this.dots.append(dot);
      });
    }
  }
  customElements.define('lc-slider', LcSlider);

  /* ------ <lc-product-form> — AJAX add to cart ----------------------------------- */

  class LcProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.form?.querySelector('[type="submit"]');
      this.errorEl = this.querySelector('[data-lc-form-error]');
      this.form?.addEventListener('submit', this.#onSubmit.bind(this));
    }

    async #onSubmit(event) {
      event.preventDefault();
      const submitter = event.submitter || this.form?.querySelector('[type="submit"]');
      if (submitter?.hasAttribute('data-loading')) return;

      submitter?.setAttribute('data-loading', '');
      this.#setError('');

      try {
        const formData = new FormData(this.form);
        await CartAPI.add([
          {
            id: Number(formData.get('id')),
            quantity: Number(formData.get('quantity') || 1),
            properties: this.#properties(formData),
            selling_plan: formData.get('selling_plan') || undefined
          }
        ]);
        announce(this.dataset.addedMessage || 'Added to cart');

        if (submitter) {
          const originalHTML = submitter.innerHTML;
          submitter.setAttribute('data-added', '');
          submitter.innerHTML = `<span style="display:inline-flex;align-items:center;gap:6px;"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" class="lc-btn-check-icon"><polyline points="20 6 9 17 4 12"/></svg> Added!</span>`;
          setTimeout(() => {
            submitter.removeAttribute('data-added');
            if (submitter.hasAttribute('data-added') === false) {
              submitter.innerHTML = originalHTML;
            }
          }, 2000);
        }

        if (submitter?.hasAttribute('data-action-checkout') || submitter?.closest('[data-action-checkout]')) {
          window.location.href = '/checkout';
          return;
        }

        if (this.dataset.openCart !== 'false') {
          document.getElementById('lc-cart-drawer')?.open(submitter);
        }
      } catch (error) {
        this.#setError(error.message);
      } finally {
        submitter?.removeAttribute('data-loading');
      }
    }

    #properties(formData) {
      const props = {};
      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && value) props[match[1]] = value;
      }
      return Object.keys(props).length ? props : undefined;
    }

    #setError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.toggleAttribute('hidden', !message);
    }
  }
  customElements.define('lc-product-form', LcProductForm);

  /* ------ <lc-cart-items> — line item updates -------------------------------------- */

  class LcCartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-lc-line]');
        if (input) this.#change(Number(input.dataset.lcLine), Number(input.value));
      });
      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-lc-remove]');
        if (remove) {
          event.preventDefault();
          this.#change(Number(remove.dataset.lcRemove), 0);
        }
      });
    }

    async #change(line, quantity) {
      this.setAttribute('aria-busy', 'true');
      try {
        const cart = await CartAPI.change(line, quantity);
        announce(
          `${cart.item_count} ${this.dataset.countLabel || 'items in cart'}`
        );
      } catch (error) {
        console.error('[LifeCare] cart change failed:', error);
      } finally {
        this.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('lc-cart-items', LcCartItems);

  /* ------ <lc-cart-note> ------------------------------------------------------------ */

  class LcCartNote extends HTMLElement {
    connectedCallback() {
      this.addEventListener(
        'input',
        debounce((event) => {
          CartAPI.update({ note: event.target.value }).catch(() => {});
        }, 500)
      );
    }
  }
  customElements.define('lc-cart-note', LcCartNote);

  /* ------ <lc-predictive-search> ------------------------------------------------------ */

  class LcPredictiveSearch extends HTMLElement {
    #controller = null;

    connectedCallback() {
      const scope = this.closest('lc-drawer') || this;
      this.input = this.querySelector('input[type="search"]');
      this.results = scope.querySelector('[data-lc-search-results]');
      this.defaultPanel = scope.querySelector('[data-lc-search-default]');
      this.input?.addEventListener(
        'input',
        debounce(() => this.#search(), 250)
      );
    }

    disconnectedCallback() {
      this.#controller?.abort();
    }

    async #search() {
      const query = this.input.value.trim();
      this.#controller?.abort();

      if (query.length < 2) {
        this.results.innerHTML = '';
        this.results.hidden = true;
        this.input.setAttribute('aria-expanded', 'false');
        if (this.defaultPanel) this.defaultPanel.hidden = false;
        return;
      }

      this.#controller = new AbortController();
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      if (this.defaultPanel) this.defaultPanel.hidden = true;
      this.setAttribute('aria-busy', 'true');

      try {
        const url = new URL(
          `${window.Shopify?.routes?.root || '/'}search/suggest`,
          location.origin
        );
        url.searchParams.set('q', query);
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set(
          'resources[type]',
          'product,collection,article,page,query'
        );
        url.searchParams.set('resources[limit]', '8');

        const response = await fetch(url, {
          signal: this.#controller.signal
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const markup = await response.text();
        const dom = new DOMParser().parseFromString(markup, 'text/html');
        const inner = dom.querySelector('[data-lc-search-results]');
        if (inner) this.results.innerHTML = inner.innerHTML;
        this.#saveRecent(query);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('[LifeCare] predictive search failed:', error);
        }
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    #saveRecent(query) {
      try {
        const recent = JSON.parse(
          localStorage.getItem('lc:recent-searches') || '[]'
        );
        const next = [query, ...recent.filter((q) => q !== query)].slice(0, 6);
        localStorage.setItem('lc:recent-searches', JSON.stringify(next));
      } catch {
        /* storage unavailable — non-critical */
      }
    }
  }
  customElements.define('lc-predictive-search', LcPredictiveSearch);

  /* ------ <lc-reveal-group> — entrance animations --------------------------------------- */

  class LcRevealGroup extends HTMLElement {
    #observer = null;
    #mutationObserver = null;

    connectedCallback() {
      if (prefersReducedMotion.matches) {
        const items = this.querySelectorAll('[data-lc-reveal]');
        for (const item of items) item.classList.add('is-revealed');
        return;
      }
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            entry.target.classList.add('is-revealed');
            this.#observer.unobserve(entry.target);
          }
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.1 }
      );

      const items = this.querySelectorAll('[data-lc-reveal]');
      if (items.length > 0) {
        this.#observeItems(items);
      }

      this.#mutationObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'childList') {
            for (const node of mutation.addedNodes) {
              if (node.nodeType === Node.ELEMENT_NODE) {
                const newItems = node.matches('[data-lc-reveal]')
                  ? [node]
                  : node.querySelectorAll('[data-lc-reveal]');
                if (newItems.length > 0) {
                  this.#observeItems(newItems);
                }
              }
            }
          }
        }
      });
      this.#mutationObserver.observe(this, { childList: true, subtree: true });
    }

    #observeItems(items) {
      let stagger = 0;
      for (const item of items) {
        if (item.classList.contains('is-revealed')) continue;
        item.style.setProperty('--lc-reveal-delay', `${stagger}ms`);
        stagger = Math.min(stagger + 70, 420);
        this.#observer.observe(item);
      }
    }

    disconnectedCallback() {
      this.#observer?.disconnect();
      this.#mutationObserver?.disconnect();
    }
  }
  customElements.define('lc-reveal-group', LcRevealGroup);

  /* ------ <lc-sticky-header> — hides on scroll down, returns on scroll up ---------------- */

  class LcStickyHeader extends HTMLElement {
    #lastY = 0;
    #onScroll = this.#handleScroll.bind(this);

    connectedCallback() {
      this.#lastY = window.scrollY;
      window.addEventListener('scroll', this.#onScroll, { passive: true });
      document.documentElement.style.setProperty('--lc-header-offset', `${this.offsetHeight}px`);
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.#onScroll);
    }

    #handleScroll() {
      const y = window.scrollY;
      this.classList.toggle('is-scrolled', y > 24);
      
      const behavior = this.getAttribute('data-behavior') || 'smart';
      
      if (behavior === 'always') {
        if (this.classList.contains('is-hidden')) {
          this.classList.remove('is-hidden');
        }
        document.documentElement.style.setProperty('--lc-header-offset', `${this.offsetHeight}px`);
        this.#lastY = y;
        return;
      }
      
      const goingDown = y > this.#lastY && y > 140;
      const drawerOpen = document.querySelector('lc-drawer[open]');
      const shouldHide = goingDown && !drawerOpen;
      this.classList.toggle('is-hidden', shouldHide);
      document.documentElement.style.setProperty('--lc-header-offset', shouldHide ? '0px' : `${this.offsetHeight}px`);
      this.#lastY = y;
    }
  }
  customElements.define('lc-sticky-header', LcStickyHeader);

  /* ------ <lc-recently-viewed> — localStorage + Section Rendering API --------------------- */

  const RECENT_KEY = 'lc:recently-viewed';

  class LcRecentlyViewed extends HTMLElement {
    async connectedCallback() {
      let handles = [];
      try {
        handles = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
        // Reset storage if it contains old handle-based data
        if (handles.some(h => isNaN(Number(h)))) {
          handles = [];
          localStorage.removeItem(RECENT_KEY);
        }
      } catch {
        /* storage unavailable */
      }
      const limit = parseInt(this.dataset.limit || '8', 10);
      handles = handles.filter((h) => h !== this.dataset.exclude).slice(0, limit);
      if (handles.length === 0) {
        this.closest('.shopify-section')?.remove();
        return;
      }
      try {
        const url = new URL(
          `${window.Shopify?.routes?.root || '/'}search`,
          location.origin
        );
        url.searchParams.set(
          'q',
          handles.map((h) => `id:${h}`).join(' OR ')
        );
        url.searchParams.set('type', 'product');
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const markup = await response.text();
        const dom = new DOMParser().parseFromString(markup, 'text/html');
        const grid = dom.querySelector('[data-lc-recent-grid]');
        const target = this.querySelector('[data-lc-recent-grid]');
        if (grid && target && grid.children.length > 0) {
          target.innerHTML = grid.innerHTML;
          this.removeAttribute('hidden');
        } else {
          this.closest('.shopify-section')?.remove();
        }
      } catch {
        this.closest('.shopify-section')?.remove();
      }
    }

    /** Called from product pages to record a visit. */
    static track(handle) {
      try {
        const items = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
        const next = [handle, ...items.filter((h) => h !== handle)].slice(0, 12);
        localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      } catch {
        /* storage unavailable */
      }
    }
  }
  customElements.define('lc-recently-viewed', LcRecentlyViewed);
  window.LcRecentlyViewed = LcRecentlyViewed;

  /* ------ Auto-submitting selects (sort, localization) --------------------------------------- */

  document.addEventListener('change', (event) => {
    if (
      event.target.matches('[data-lc-auto-submit]') ||
      event.target.closest('form[data-lc-auto-submit]')
    ) {
      event.target.closest('form')?.submit();
    }
  });

  /* ------ Details disclosure: close on outside click (mega menu, sort menus) --------------- */

  document.addEventListener('click', (event) => {
    for (const details of document.querySelectorAll(
      'details[data-lc-close-outside][open]'
    )) {
      if (!details.contains(event.target)) details.removeAttribute('open');
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const details = event.target.closest('details[data-lc-close-outside][open]');
    if (details) {
      details.removeAttribute('open');
      details.querySelector('summary')?.focus();
    }
  });
})();
